"use client";

import { useState } from "react";
import Link from "next/link";
import { IconCheck, IconAlertTriangle, IconInfoCircle, IconCircleCheck, IconAlertCircle, IconBellOff } from "@tabler/icons-react";
import { getNotificationFeed, markNotificationsRead, type NotificationFeed, type NotificationView } from "@/server/actions/notifications";
function relativeTime(iso: string): string {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "gerade eben";
  const m = Math.floor(s / 60);
  if (m < 60) return `vor ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} h`;
  return `vor ${Math.floor(h / 24)} d`;
}
function levelIcon(level: string) {
  switch (level) {
    case "success":
      return <IconCircleCheck size={18} style={{
        color: "#6ee7b7"
      }} />;
    case "warning":
      return <IconAlertTriangle size={18} style={{
        color: "#fcd34d"
      }} />;
    case "error":
      return <IconAlertCircle size={18} style={{
        color: "#fca5a5"
      }} />;
    default:
      return <IconInfoCircle size={18} style={{
        color: "#93c5fd"
      }} />;
  }
}
function hrefFor(n: NotificationView): string | null {
  const imdbId = typeof n.meta?.imdbId === "string" ? n.meta.imdbId as string : null;
  if (imdbId) return `/${imdbId}`;
  if (n.entityType === "series" || n.entityType === "season" || n.entityType === "episode") return "/watchlist";
  return null;
}
export function NotificationsClient({
  initialFeed
}: {
  initialFeed: NotificationFeed;
}) {
  const [items, setItems] = useState<NotificationView[]>(initialFeed.items);
  const [cursor, setCursor] = useState<string | null>(initialFeed.nextCursor);
  const [unread, setUnread] = useState(initialFeed.unreadCount);
  const [loading, setLoading] = useState(false);
  async function loadMore() {
    if (!cursor || loading) return;
    setLoading(true);
    try {
      const feed = await getNotificationFeed({
        cursor,
        limit: 30
      });
      setItems(prev => [...prev, ...feed.items]);
      setCursor(feed.nextCursor);
      setUnread(feed.unreadCount);
    } finally {
      setLoading(false);
    }
  }
  async function markAll() {
    const c = await markNotificationsRead();
    setUnread(c);
    setItems(prev => prev.map(n => ({
      ...n,
      readAt: n.readAt ?? new Date().toISOString()
    })));
  }
  async function markOne(id: string) {
    const c_0 = await markNotificationsRead(id);
    setUnread(c_0);
    setItems(prev_0 => prev_0.map(n_0 => n_0.id === id ? {
      ...n_0,
      readAt: n_0.readAt ?? new Date().toISOString()
    } : n_0));
  }
  return <div className="admin-page d-flex flex-column flex-grow-1 min-w-0">
      <div className="admin-page-inner">
        <div className="admin-page-header d-flex align-items-start justify-content-between">
          <div>
            <div className="admin-eyebrow">Aktivität</div>
            <h1>Benachrichtigungen</h1>
            <p>{unread > 0 ? `${unread} ungelesen` : "Alles gelesen."}</p>
          </div>
          {unread > 0 && <button type="button" onClick={markAll} className="btn btn-sm d-inline-flex align-items-center gap-2" style={{
          background: "var(--colors-bg-subtle)",
          border: "1px solid var(--colors-border-default)",
          color: "var(--colors-fg-muted)"
        }}>
              <IconCheck size={14} /> Alle als gelesen
            </button>}
        </div>

        {items.length === 0 ? <div className="d-flex flex-column align-items-center justify-content-center gap-2 py-5" style={{
        color: "var(--colors-fg-muted)"
      }}>
            <IconBellOff size={32} />
            <p className="mb-0">Noch keine Benachrichtigungen.</p>
          </div> : <div className="d-flex flex-column gap-2 w-100 mx-auto" style={{
        maxWidth: 820
      }}>
            {items.map(n_1 => {
          const href = hrefFor(n_1);
          const inner =
          // biome-ignore lint/correctness/useJsxKeyInIterable: der Key sitzt auf dem umschliessenden Element unten (key={n.id}), nicht hier
          <div className="d-flex align-items-start gap-3 p-3 rounded-2" style={{
            background: n_1.readAt ? "var(--colors-bg-subtle)" : "rgba(59,130,246,0.08)",
            border: "1px solid var(--colors-border-default)"
          }}>
                  <div style={{
              marginTop: 2
            }}>{levelIcon(n_1.level)}</div>
                  <div className="flex-grow-1" style={{
              minWidth: 0
            }}>
                    <div style={{
                color: "var(--colors-fg-default)",
                fontWeight: n_1.readAt ? 400 : 600
              }}>{n_1.title}</div>
                    {n_1.body && <div className="small mt-1" style={{
                color: "var(--colors-fg-muted)"
              }}>{n_1.body}</div>}
                    <div className="mt-1" style={{
                fontSize: 11,
                color: "var(--colors-fg-subtle)"
              }}>
                      {relativeTime(n_1.createdAt)} · {n_1.type}
                    </div>
                  </div>
                  {!n_1.readAt && <button type="button" onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              void markOne(n_1.id);
            }} title="Als gelesen markieren" className="btn btn-sm p-1" style={{
              background: "transparent",
              border: "none",
              color: "var(--colors-fg-subtle)"
            }}>
                      <IconCheck size={16} />
                    </button>}
                </div>;
          return href ? <Link key={n_1.id} href={href} className="text-decoration-none" onClick={() => void markOne(n_1.id)}>
                  {inner}
                </Link> : <div key={n_1.id}>{inner}</div>;
        })}

            {cursor && <button type="button" onClick={loadMore} disabled={loading} className="btn btn-sm align-self-center mt-2" style={{
          background: "var(--colors-bg-subtle)",
          border: "1px solid var(--colors-border-default)",
          color: "var(--colors-fg-muted)"
        }}>
                {loading ? "Lädt…" : "Mehr laden"}
              </button>}
          </div>}
      </div>
    </div>;
}